<div class="employees">
  <div class="employees__container">
    @if (loading()) {
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    }
    @if (!loading()) {
      <div class="employees__header">
        <h1 class="employees__header-label navy-text">
          {{'My Employees'}} {{('(' + numEmployees + ')')}}
        </h1>
        @if (numEmployees && !viewOnly) {
          <div class="employees__header-button-container">
            <button data-pw="employee-add-button" 
                    mat-raised-button class="employees__header-button"
                    (click)="onAddEmployeeClick()">
              Add employee
            </button>
            <span class="employees__header-button-description">
              Only list employees who will be providing services
            </span>
          </div>
        }
      </div>
      <div class="employees__filter-form">
        <!-- Employee Filters -->
          <form [formGroup]="employeeFilterForm">
            <div class="grid employees__filter-grid">
              <div class="employees__filter-col col-4">
                <div class="employees__filter-field">
                  <label class="employees__filter-label" for="employeeNameInput">Employee Name</label>
                  <mat-form-field appearance="outline" class="employees__filter-form-field">
                    <mat-select
                      aria-label="Employee"
                      data-pw="filter-employee-name" 
                      formControlName="employeeName"
                      placeholder="Select employee"
                      (selectionChange)="onApplyFilters()">
                      <mat-option>
                        <ngx-mat-select-search
                          noEntriesFoundLabel="No matching names found"
                          placeholderLabel="Search employee name"
                          [formControl]="employeeNameSearchCtrl">
                        </ngx-mat-select-search>
                      </mat-option>
                      <mat-option data-pw="employee-all" value="">All</mat-option>
                        @for (employee of employeesFilteredList(); track $index) {
                          <mat-option [attr.data-pw]="'employee-name-' + $index" [value]="employee">
                          {{ employee }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  </div>
                </div>
            
              <div class="employees__filter-col col-2">
                <div class="employees__filter-field">
                  <label class="employees__filter-label" for="subServiceInput">Sub-service</label>
                  <mat-form-field appearance="outline" class="employees__filter-form-field">
                    <mat-select
                      aria-label="Sub-service"
                      data-pw="filter-sub-service" 
                      formControlName="subService"
                      placeholder="Select a subservice"
                      (selectionChange)="onApplyFilters()">
                      <mat-option data-pw="sub-service-all" value="">All</mat-option>
                        @for (subService of subServicesList(); track subService.id) {
                          <mat-option [attr.data-pw]="'sub-service-name-' + subService.id" [value]="subService">
                            {{ subService.name }}
                          </mat-option>
                        }
                      </mat-select>
                  </mat-form-field>
                </div> 
              </div>
            
              <div class="employees__filter-col col-2">
                <div class="employees__filter-field">
                  <label class="employees__filter-label" for="validationStatusInput">Status</label>
                  <mat-form-field appearance="outline" class="employees__filter-form-field">
                    <mat-select data-pw="filter-validation-status" 
                                formControlName="validationStatus" 
                                placeholder="Select a Status"
                                (selectionChange)="onApplyFilters()">
                      <mat-option data-pw="validation-status-all" value="">All</mat-option>
                      @for (status of statusList; track $index) {
                        <mat-option [value]="status">{{ status.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
              </div>

              <div class="apply-button employees__filter-col col-2">
                <button data-pw="apply-filters" mat-flat-button (click)="onClearFilters()">
                  Clear Filters
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Employee Table -->
        <cfi-mat-table
          data-pw="employee-table"
          [columnDefinitions]="columnDefinitions"
          [currentPageSize]="pageSize"
          [data]="employeeFilteredData()"
          [displayedColumns]="displayedColumns"
          (cbPaginate)="updatePageSize($event)">

          <!-- Header Template -->
          <ng-template #headerTemplate let-column>
            @if (column.name === 'action') {
              <div class="action-column">
                <span class="centered">Action</span>
              </div>
            } @else {
              {{ column.header }}
            }
          </ng-template>

          <!-- Cell Template -->
          <ng-template #cellTemplate let-employee let-column="column">
            @switch (column.name) {
              <!-- Default Column -->
              @default {
                <td class="table-cell__icon-container"
                    [attr.data-pw]="'cell-' + column.name" 
                    [style.width.%]="column.width">
                  {{ employee[column.name] | truncate: 32 }}
                </td>
              }

              <!-- Name Columns -->
              @case ('name') {
                <button class="hyperlink" type="button" (click)="onEditClick(employee)">
                  {{ employee[column.name] }}
                </button>
              }

              <!-- Provider Type Column -->
              @case ('type') {
                <td class="table-cell__icon-container"
                    [attr.data-pw]="'cell-' + column.name"
                    [style.width.%]="column.width">
                  {{ getProviderTypeDisplay(employee.type) | truncate: 32 }}
                </td>
              }

              <!-- Validated Column -->
              @case ('validationStatus') {
                <td [attr.data-pw]="'cell-' + column.name" [style.width.%]="column.width">
                  <b>{{ employee[column.name] }}</b>
                </td>
              }

              <!-- Subservice Column -->
              @case ('subServices') {
                <td [attr.data-pw]="'cell-' + column.name" [style.width.%]="column.width">
                  @for (service of employee[column.name]; track $index) {
                    <div>{{ service.name }}</div>
                  }
                </td>
              }

              <!-- Action Column -->
              @case ('action') {
                @if (!viewOnly) {
                  <td class="employee-action"
                      [attr.data-pw]="'cell-' + column.name" 
                      [style.width.%]="column.width">
                    <button
                        aria-label="Edit employee"
                        class="employee-action-button table-cell__action-button"
                        data-qa="edit-employee-button"
                        mat-icon-button
                        (click)="onEditClick(employee)">
                      <mat-icon class="mat-action-icon" svgIcon="k12-edit"></mat-icon>
                    </button>

                    <button
                        aria-label="Delete employee"
                        class="employee-action-button table-cell__action-button"
                        data-qa="delete-employee-button"
                        mat-icon-button
                        (click)="onDeleteClick(employee)">
                      <mat-icon class="mat-action-icon" svgIcon="k12-delete"></mat-icon>
                    </button>
                  </td>
                }
              }
            }
          </ng-template>
        </cfi-mat-table>
    }
  </div>
</div>